import { useMemo } from "react";
import {
  Background, ControlButton, Controls, Handle, MiniMap, Position, ReactFlow,
  type Edge, type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Dagre from "@dagrejs/dagre";
import { useGraphFullscreen } from "./useGraphFullscreen";
import { SectionCard } from "../components/ui";
import { useParcelGraph } from "./hooks";
import type { ParcelGraph, ParcelGraphNode } from "./types";

const STATUS_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  ok: { bg: "#dcfce7", text: "#166534", label: "uzgodniona" },
  action_required: { bg: "#fef3c7", text: "#92400e", label: "wymaga działania" },
  application_sent: { bg: "#dbeafe", text: "#1e40af", label: "wniosek" },
  negotiator_sent: { bg: "#f3e8ff", text: "#6b21a8", label: "negocjator" },
  in_progress: { bg: "#dbeafe", text: "#1e40af", label: "w toku" },
  problem: { bg: "#fee2e2", text: "#991b1b", label: "problem" },
  not_started: { bg: "#f8fafc", text: "#64748b", label: "nie rozpoczęto" },
};

function ParcelNode({ data }: { data: ParcelGraphNode }) {
  const st = STATUS_STYLE[data.status ?? "not_started"] ?? STATUS_STYLE.not_started;
  return (
    <div className="rounded-md border px-2.5 py-1.5 shadow-sm text-xs" style={{ width: 132, background: st.bg, color: st.text, borderColor: data.route_review ? "#ea580c" : "rgba(0,0,0,0.12)", borderWidth: data.route_review ? 2 : 1 }}>
      <Handle type="target" position={Position.Left} style={{ background: "#94a3b8" }} />
      <div className="font-bold truncate">dz. {data.label}</div>
      <div className="opacity-80 text-[10px]">{st.label}</div>
    </div>
  );
}

function OwnerNode({ data }: { data: ParcelGraphNode }) {
  const multi = (data.parcel_count ?? 0) > 1;
  return (
    <div className="rounded-full border px-3 py-1.5 shadow-sm text-xs" style={{ width: 168, background: multi ? "#fff7ed" : "#f8fafc", color: "#334155", borderColor: multi ? "#ea580c" : "rgba(0,0,0,0.14)", borderWidth: multi ? 2 : 1 }}>
      <div className="font-semibold truncate">{data.label}</div>
      {multi && <div className="text-[10px] font-mono" style={{ color: "#ea580c" }}>◆ {data.parcel_count} działek — jedna zgoda</div>}
      <Handle type="source" position={Position.Right} style={{ background: "#94a3b8" }} />
    </div>
  );
}

const nodeTypes = { parcel: ParcelNode, owner: OwnerNode };

function layout(graph: ParcelGraph): { nodes: Node[]; edges: Edge[] } {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 12, ranksep: 90, marginx: 12, marginy: 12 });
  graph.nodes.forEach((n) => g.setNode(n.id, { width: n.type === "owner" ? 168 : 132, height: n.type === "owner" ? 52 : 48 }));
  graph.edges.forEach((e) => g.setEdge(e.source, e.target));
  Dagre.layout(g);
  const nodes: Node[] = graph.nodes.map((n) => {
    const p = g.node(n.id);
    return { id: n.id, type: n.type, position: { x: p.x - (n.type === "owner" ? 84 : 66), y: p.y - 24 }, data: n as unknown as Record<string, unknown> };
  });
  const edges: Edge[] = graph.edges.map((e, i) => ({
    id: `e${i}`, source: e.source, target: e.target,
    style: { stroke: "#94a3b8", strokeWidth: 1.4 },
    markerEnd: { type: "arrowclosed" as const, color: "#94a3b8" },
  }));
  return { nodes, edges };
}

export function ParcelGraphView({ connectionId, active }: { connectionId: string; active: boolean }) {
  const { data, isLoading } = useParcelGraph(connectionId, active);
  const { isFull, toggle: toggleFull } = useGraphFullscreen();
  const { nodes, edges } = useMemo(() => (data ? layout(data) : { nodes: [], edges: [] }), [data]);
  const owners = data?.nodes.filter((n) => n.type === "owner").length ?? 0;
  const parcels = data?.nodes.filter((n) => n.type === "parcel").length ?? 0;
  return (
    <SectionCard
      title="Graf działek — właściciel jako hub"
      action={<span className="text-xs text-[var(--color-text-muted)]">{parcels} działek · {owners} właścicieli</span>}
    >
      {isLoading ? (
        <div className="text-sm text-[var(--color-text-muted)]">Ładowanie…</div>
      ) : parcels === 0 ? (
        <div className="text-sm text-[var(--color-text-muted)]">Brak działek na aktualnej trasie.</div>
      ) : (
        <>
          <div className={isFull
            ? "fixed inset-0 z-40 bg-[var(--color-bg-elevated)]"
            : "h-[560px] rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"}>
            <ReactFlow key={isFull ? "fs" : "inline"} nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView colorMode="system" proOptions={{ hideAttribution: true }}>
              <Background gap={16} />
              <Controls showInteractive={false}>
                <ControlButton onClick={toggleFull} title={isFull ? "Zamknij pełny ekran (Esc)" : "Pełny ekran"}>
                  {isFull ? "✕" : "⛶"}
                </ControlButton>
              </Controls>
              <MiniMap pannable zoomable />
            </ReactFlow>
          </div>
          <div className="text-[10px] font-mono text-[var(--color-text-muted)] mt-2 flex flex-wrap gap-3">
            <span style={{ color: "#ea580c" }}>◆ pomarańczowy hub = właściciel wielodziałkowy (domknij raz)</span>
            <span>kolor działki = status</span>
          </div>
        </>
      )}
    </SectionCard>
  );
}
